import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, onSnapshot } from 'firebase/firestore';

// --- Konfigurasi Firebase dan Variabel Global ---
const FALLBACK_FIREBASE_CONFIG = {
    apiKey: "AIzaSyCFRCcRa0GHbN1VZeQYiso_R4XyPJuSDfc",
    authDomain: "report-fd0df.firebaseapp.com",
    projectId: "report-fd0df",
    storageBucket: "report-fd0df.appspot.com",
    messagingSenderId: "556647290903",
    appId: "1:556647290903:web:be98df24e9f2801b3f6452",
    measurementId: "G-6C51KSLYHR"
};

// --- KREDENSIAL TELEGRAM PERMANEN ---
const PERMANENT_TELEGRAM_TOKEN = '8444940207:AAHpr9IyJboVIEA8ltqg67JoXK8E5aSJhGQ';
// ID Grup yang berbeda untuk pengiriman laporan
const PERMANENT_CHAT_ID_GRUP1 = '-1002410368560';
const PERMANENT_CHAT_ID_GRUP2 = '-1002468968890';
const PERMANENT_THREAD_ID = '40045'; // Hanya untuk Grup 1


const getGlobalVar = (name, fallback) => {
    const globalContext = typeof window !== 'undefined' ? window : {};
    return Object.prototype.hasOwnProperty.call(globalContext, name) ? globalContext[name] : fallback;
};

const appId = getGlobalVar('__app_id', 'default-app-id');
const firebaseConfigRaw = getGlobalVar('__firebase_config', JSON.stringify(FALLBACK_FIREBASE_CONFIG));
const firebaseConfig = JSON.parse(firebaseConfigRaw);
const initialAuthToken = getGlobalVar('__initial_auth_token', null);

// --- Fungsi Bantuan ---
const formatTime = (date) => new Date(date).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });

// Fungsi untuk escape karakter Markdown V2, kecuali titik agar tidak merusak URL
const escapeMd = (text) => text ? text.replace(/([_*`\[\]()~>#+=\-|{}!])/g, '\\$1') : '';


// --- Komponen Gaya Global & Latar Belakang Aurora ---
const GlobalStyles = () => (
    <style>{`
        body { 
            font-family: 'Inter', sans-serif; 
            background-color: #0d1117;
            color: #e6edf3;
            position: relative;
        }
        
        body::before, body::after {
            content: '';
            position: absolute;
            width: 500px;
            height: 500px;
            background-image: radial-gradient(circle, rgba(0, 128, 255, 0.4) 0%, rgba(0, 128, 255, 0) 70%);
            border-radius: 50%;
            filter: blur(100px);
            z-index: -1;
            animation: move-glow 20s infinite alternate;
        }

        body::before { top: -20%; left: -20%; animation-delay: -10s; }
        body::after { bottom: -20%; right: -20%; background-image: radial-gradient(circle, rgba(138, 43, 226, 0.4) 0%, rgba(138, 43, 226, 0) 70%); }

        @keyframes move-glow {
            0% { transform: translate(0, 0) scale(1); }
            50% { transform: translate(100px, 50px) scale(1.2); }
            100% { transform: translate(-50px, -100px) scale(1); }
        }
        
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
        
        .glass-card { 
            background: rgba(22, 27, 34, 0.75); 
            backdrop-filter: blur(12px) saturate(180%);
            -webkit-backdrop-filter: blur(12px) saturate(180%);
            color: #e6edf3; 
            border-radius: 1.25rem; 
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2); 
            border: 1px solid rgba(139, 148, 158, 0.2);
            padding: 2.5rem;
        }
        
        .dark-input { 
            background-color: rgba(13, 17, 23, 0.8); 
            color: #e6edf3; 
            border: 1px solid #30363d; 
            transition: border-color 0.2s, box-shadow 0.2s;
            border-radius: 0.5rem;
        }
        
        .dark-input:focus { border-color: #2f81f7; box-shadow: 0 0 0 4px rgba(47, 129, 247, 0.3); outline: none; }
        .dark-label { color: #8b949e; font-weight: 500; }
        .dark-select { appearance: none; }
        .section-divider { border-top: 1px solid rgba(139, 148, 158, 0.2); padding-top: 1.5rem; margin-top: 1.5rem; }
        .section-title { display: flex; align-items: center; font-size: 1.25rem; font-weight: 700; margin-bottom: 1.5rem; color: #c9d1d9; }
        .section-icon { margin-right: 0.75rem; width: 1.5rem; height: 1.5rem; }
        
        .log-success { background-color: rgba(31, 111, 46, 0.2); border-left: 4px solid #3fb950; color: #afb8c1; }
        .log-failure { background-color: rgba(215, 58, 73, 0.2); border-left: 4px solid #f85149; color: #afb8c1; }

        .main-title { background: -webkit-linear-gradient(45deg, #a5b4fc, #5eead4); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .form-section-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
        @media (min-width: 768px) { .form-section-grid { grid-template-columns: repeat(2, 1fr); } }
    `}</style>
);

// --- Komponen-Komponen UI ---
const Icon = React.memo(({ path, colorClass = 'text-cyan-400' }) => (
    <svg className={`section-icon ${colorClass}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d={path} /></svg>
));

const Header = React.memo(({ userId }) => (
    <div className="glass-card p-4 flex justify-center items-center flex-wrap gap-2 animate-fadeIn">
        <div>
            <p className="font-medium text-gray-400">ID Pengguna: <span className="font-mono break-all text-sm text-cyan-400">{userId}</span></p>
        </div>
    </div>
));

const Notification = React.memo(({ message, type, show }) => {
    if (!show) return null;
    const baseStyle = "fixed bottom-5 right-5 p-4 rounded-lg shadow-xl text-white font-semibold animate-fadeIn border";
    const typeStyle = type === 'success' ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500';
    return <div className={`${baseStyle} ${typeStyle}`}>{message}</div>;
});

const ManualReportForm = React.memo(({ formState, onFormChange, onSubmit, isSubmitting }) => (
    <div className="glass-card animate-fadeIn" style={{ animationDelay: '200ms' }}>
        <h2 className="text-3xl font-black border-b border-gray-700 pb-4 mb-6 text-cyan-400">LAPORAN SEO MANUAL</h2>
        <form onSubmit={onSubmit} className="space-y-6">
            <div>
                <label htmlFor="storeName" className="block text-sm dark-label mb-1">Nama Toko (Header Laporan)</label>
                <input id="storeName" type="text" name="storeName" value={formState.storeName} onChange={onFormChange} placeholder="Contoh: PIKASLOT OFFICIAL (wajib)" className="mt-1 block w-full shadow-sm p-3 dark-input" required />
            </div>
             <div className="section-divider">
                <h3 className="section-title"><Icon path="M12.0002 11.293L14.1215 9.17175L12.7073 7.75754L10.5859 9.87891L8.46462 7.75754L7.05041 9.17175L9.17178 11.293L7.05041 13.4144L8.46462 14.8286L10.5859 12.7072L12.7073 14.8286L14.1215 13.4144L12.0002 11.293ZM12.0002 2.00002C17.523 2.00002 22.0002 6.47717 22.0002 12C22.0002 17.5228 17.523 22 12.0002 22C6.47732 22 2.00017 17.5228 2.00017 12C2.00017 6.47717 6.47732 2.00002 12.0002 2.00002Z" />I. Google</h3>
                <div className="form-section-grid">
                    <div>
                        <label htmlFor="rankMs" className="block text-sm dark-label mb-1">Peringkat MS</label>
                        <select id="rankMs" name="rankMs" value={formState.rankMs} onChange={onFormChange} className="mt-1 block w-full shadow-sm p-3 dark-input dark-select cursor-pointer"><option value="1">1</option><option value="2">2</option><option value="3">3</option><option value="Hilang dari Google">Hilang dari Google</option></select>
                    </div>
                    <div>
                        <label htmlFor="statusIndikatif" className="block text-sm dark-label mb-1">Status Indikatif</label>
                        <select id="statusIndikatif" name="statusIndikatif" value={formState.statusIndikatif} onChange={onFormChange} className="mt-1 block w-full shadow-sm p-3 dark-input dark-select cursor-pointer"><option value="Aktif">Aktif</option><option value="Nawala">Nawala</option></select>
                    </div>
                    <div className="md:col-span-2">
                        <label htmlFor="selectedDomainName" className="block text-sm dark-label mb-1">Nama Domain (Domain MS)</label>
                        <input id="selectedDomainName" type="text" name="selectedDomainName" value={formState.selectedDomainName} onChange={onFormChange} placeholder="Masukkan domain utama (wajib)" className="mt-1 block w-full shadow-sm p-3 dark-input" required />
                    </div>
                </div>
            </div>
            <div className="section-divider">
                 <h3 className="section-title"><Icon path="M11 7h2v5h-2V7zm0 7h2v2h-2v-2zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" colorClass="text-green-400"/>II. AMP</h3>
                <div className="form-section-grid">
                     <div>
                        <label htmlFor="statusAmp" className="block text-sm dark-label mb-1">Status AMP</label>
                        <select id="statusAmp" name="statusAmp" value={formState.statusAmp} onChange={onFormChange} className="mt-1 block w-full shadow-sm p-3 dark-input dark-select cursor-pointer"><option value="Aktif">Aktif</option><option value="Tidak Aktif">Tidak Aktif</option></select>
                    </div>
                    <div>
                        <label htmlFor="checkIndikatif" className="block text-sm dark-label mb-1">Status Tautan Cache</label>
                        <select id="checkIndikatif" name="checkIndikatif" value={formState.checkIndikatif} onChange={onFormChange} className="mt-1 block w-full shadow-sm p-3 dark-input dark-select cursor-pointer"><option value="Aktif">Aktif</option><option value="Tidak Aktif">Tidak Aktif</option></select>
                    </div>
                    <div>
                        <label htmlFor="domainAmp" className="block text-sm dark-label mb-1">Domain AMP</label>
                        <input type="text" id="domainAmp" name="domainAmp" value={formState.domainAmp} onChange={onFormChange} placeholder="Domain AMP" className="mt-1 block w-full shadow-sm p-3 dark-input" />
                    </div>
                    <div>
                        <label htmlFor="linkCache" className="block text-sm dark-label mb-1">Tautan Cache</label>
                        <input type="text" id="linkCache" name="linkCache" value={formState.linkCache} onChange={onFormChange} placeholder="google.com/search/cache/..." className="mt-1 block w-full shadow-sm p-3 dark-input" />
                    </div>
                    <div className="md:col-span-2">
                        <label htmlFor="tombolLoginDaftar" className="block text-sm dark-label mb-1">Tombol Masuk / Daftar</label>
                        <input type="text" id="tombolLoginDaftar" name="tombolLoginDaftar" value={formState.tombolLoginDaftar} onChange={onFormChange} placeholder="Tautan/status tombol (cth. Berfungsi Normal)" className="mt-1 block w-full shadow-sm p-3 dark-input" />
                    </div>
                </div>
            </div>
            <div className="section-divider">
                <h3 className="section-title"><Icon path="M17 7h-4v2h4c1.65 0 3 1.35 3 3s-1.35 3-3 3h-4v2h4c2.76 0 5-2.24 5-5s-2.24-5-5-5zm-6 8H7c-1.65 0-3-1.35-3-3s1.35-3 3-3h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-2zm-3-4h8v2H8z" colorClass="text-yellow-400" />III. Skrip Head & IV. Tautan Iklan</h3>
                <div className="form-section-grid">
                    <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-yellow-300">III. Skrip Head MS</h4>
                        <div><label htmlFor="canonical" className="block text-sm dark-label mb-1">Kanonikal</label><input type="text" id="canonical" name="canonical" value={formState.canonical} onChange={onFormChange} placeholder="Tautan Kanonikal" className="mt-1 block w-full shadow-sm p-3 dark-input" /></div>
                        <div><label htmlFor="amphtmlTag" className="block text-sm dark-label mb-1">Tag amphtml</label><input type="text" id="amphtmlTag" name="amphtmlTag" value={formState.amphtmlTag} onChange={onFormChange} placeholder="Tautan amphtml" className="mt-1 block w-full shadow-sm p-3 dark-input" /></div>
                    </div>
                    <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-red-300">IV. Tautan Iklan</h4>
                        <div><label htmlFor="domainAds" className="block text-sm dark-label mb-1">Domain Iklan</label><input type="text" id="domainAds" name="domainAds" value={formState.domainAds} onChange={onFormChange} placeholder="Domain Iklan" className="mt-1 block w-full shadow-sm p-3 dark-input" /></div>
                        <div><label htmlFor="shortlink" className="block text-sm dark-label mb-1">Tautan Pendek</label><input type="text" id="shortlink" name="shortlink" value={formState.shortlink} onChange={onFormChange} placeholder="Tautan Pendek" className="mt-1 block w-full shadow-sm p-3 dark-input" /></div>
                    </div>
                </div>
            </div>
            <div className="section-divider">
                <h3 className="section-title"><Icon path="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2zm0 15.17L18.83 16H4V4h16v13.17zM6 12h12v2H6zm0-3h12v2H6zm0-3h12v2H6z" colorClass="text-gray-400"/>Catatan</h3>
                <textarea id="notes" name="notes" rows="3" value={formState.notes} onChange={onFormChange} placeholder="Catatan tambahan..." className="mt-1 block w-full shadow-sm p-3 dark-input"></textarea>
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full px-4 py-3 text-white font-bold rounded-lg shadow-lg transition duration-150 disabled:bg-gray-500 disabled:shadow-none flex items-center justify-center bg-cyan-600 hover:bg-cyan-500 mt-8">
                {isSubmitting ? 'Mengirim...' : 'Kirim Laporan Manual'}
            </button>
        </form>
    </div>
));


// --- Komponen Utama Aplikasi ---
const App = () => {
    const [db, setDb] = useState(null);
    const [userId, setUserId] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isReportingManual, setIsReportingManual] = useState(false);
    const [notification, setNotification] = useState({ message: '', type: '', show: false });

    const initialFormState = useMemo(() => ({
        selectedDomainName: '', storeName: '', rankMs: '1', statusIndikatif: 'Aktif', statusAmp: 'Aktif',
        domainAmp: '', tombolLoginDaftar: '', linkCache: '', checkIndikatif: 'Aktif', canonical: '',
        amphtmlTag: '', domainAds: '', shortlink: '', notes: '',
    }), []);
    const [manualReportForm, setManualReportForm] = useState(initialFormState);
    
    const showNotification = useCallback((message, type = 'success') => {
        setNotification({ message, type, show: true });
        setTimeout(() => setNotification({ message: '', type: '', show: false }), 3000);
    }, []);

    useEffect(() => {
        try {
            const app = initializeApp(firebaseConfig);
            const auth = getAuth(app);
            const unsubscribe = onAuthStateChanged(auth, async (user) => {
                let currentUid = user?.uid;
                if (!currentUid) {
                    if (initialAuthToken) {
                        try { currentUid = (await signInWithCustomToken(auth, initialAuthToken)).user.uid; } catch (e) { console.error("Gagal masuk dengan token kustom, beralih ke anonim", e); currentUid = (await signInAnonymously(auth)).user.uid; }
                    } else {
                        try { currentUid = (await signInAnonymously(auth)).user.uid; } catch (e) { console.error("Gagal masuk secara anonim:", e); }
                    }
                }
                setUserId(currentUid);
                setIsAuthReady(true);
                setLoading(false);
            });
            return () => unsubscribe();
        } catch (e) { console.error("Gagal menginisialisasi Firebase:", e); setLoading(false); }
    }, []);


    const sendTelegramReport = useCallback(async (reportText, token, chatId, threadId) => {
        if (!token || !chatId) return { success: false, message: 'Token atau ID Obrolan belum diatur.' };
        const apiUrl = `https://api.telegram.org/bot${token}/sendMessage`;
        const payload = { chat_id: chatId, text: reportText, parse_mode: 'Markdown', ...(threadId && { message_thread_id: threadId }) };
        try {
            const response = await fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (response.ok) return { success: true, message: 'Laporan berhasil dikirim.' };
            const result = await response.json(); return { success: false, message: `Gagal: ${result.description}` };
        } catch (error) { return { success: false, message: `Error jaringan: ${error.message}` }; }
    }, []);
    
    const runManualDetailReport = useCallback(async (e) => {
        e.preventDefault(); if (isReportingManual) return; setIsReportingManual(true);
        const d = manualReportForm; const now = new Date();
        const formatStatus = (s) => s === 'Aktif' ? `✅ ${s}` : `⛔ ${s}`;
        const tokoDicek = (d.storeName || d.selectedDomainName || 'N/A').toUpperCase();
        const reportText = `*🌟 LAPORAN STATUS SEO DOMAIN 🌟*
---
*📝 ${escapeMd(tokoDicek)}*

*📅 Waktu Laporan:* ${now.toLocaleString('id-ID')}

*============================*
*🔍 I. STATUS GOOGLE (RANK MS)*
*============================*

*🔹 Rank MS:* ${d.rankMs}

*🔹 Domain MS:* ${d.selectedDomainName || 'N/A'}

*🔹 Status Indikatif:* *${formatStatus(d.statusIndikatif)}*

*=================================*

*⚡️ II. STATUS AMP & CACHE*

*=================================*

*🔹 Status AMP:* *${formatStatus(d.statusAmp)}*
*🔹 Domain AMP:* ${d.domainAmp || 'N/A'}
*🔹 Link Cache:* ${d.linkCache || 'N/A'}
*🔹 Status Link Cache:* *${formatStatus(d.checkIndikatif)}*
*🔹 Tombol Masuk/Daftar:* ${d.tombolLoginDaftar || 'N/A'}

*=================================*

*🏷️ III & IV. HEAD SCRIPT & LINK ADS*

*=================================*

*• Canonical:* ${d.canonical || 'N/A'}
*• Amphtml Tag:* ${d.amphtmlTag || 'N/A'}
*• Domain Ads:* ${d.domainAds || 'N/A'}
*• Shortlink:* ${d.shortlink || 'N/A'}

*============================*

*💬 CATATAN TAMBAHAN (Notes)*

*============================*

${d.notes ? escapeMd(d.notes) : '_Tidak ada catatan._'}`.trim();

        const groupsToSend = [
            { id: PERMANENT_CHAT_ID_GRUP1, name: "Grup 1", threadId: PERMANENT_THREAD_ID },
            { id: PERMANENT_CHAT_ID_GRUP2, name: "Grup 2", threadId: null }
        ];

        const reportPromises = groupsToSend.map(group =>
            sendTelegramReport(reportText, PERMANENT_TELEGRAM_TOKEN, group.id, group.threadId)
        );

        const results = await Promise.allSettled(reportPromises);
        const isOverallSuccess = results.every(res => res.status === 'fulfilled' && res.value.success);
        
        const finalNotificationMessage = isOverallSuccess ? 'Laporan berhasil dikirim ke semua grup.' : 'Beberapa laporan gagal terkirim.';
       
        if (isOverallSuccess) {
            setManualReportForm(initialFormState);
        }
        setIsReportingManual(false); 
        showNotification(finalNotificationMessage, isOverallSuccess ? 'success' : 'error');
    }, [manualReportForm, sendTelegramReport, showNotification, isReportingManual, initialFormState]);
    
    const handleManualFormChange = (e) => setManualReportForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

    if (loading) return <div className="flex justify-center items-center h-screen bg-gray-900 text-white text-xl">Memuat Aplikasi...</div>;

    return (
        <React.Fragment>
            <GlobalStyles />
            <Notification {...notification} />
            <div className="min-h-screen p-4 sm-p-8">
                <div className="max-w-4xl mx-auto space-y-8">
                    <h1 className="text-4xl lg:text-5xl font-extrabold text-white text-center drop-shadow-lg main-title animate-fadeIn">🚀 Manajer Laporan SEO Otomatis</h1>
                    {userId && <Header userId={userId} />}
                    <ManualReportForm formState={manualReportForm} onFormChange={handleManualFormChange} onSubmit={runManualDetailReport} isSubmitting={isReportingManual} />
                </div>
            </div>
        </React.Fragment>
    );
};

export default App;